// frontend/src/pages/ToolsHub.js
//
// /tools — the parent for every free tool.
//
// SAME BUG AS /for, FOUND 2026-08-18. The prerenderer writes
// build/tools/<slug>/index.html, so /tools is a real directory with no index
// file — and htaccess.template sets Options -Indexes, which turns that into a
// 403 rather than a 404. Measured before this page existed:
//     https://new.greatlysocial.com/tools   -> 403
//     https://new.greatlysocial.com/tools/  -> 403
// The parent path of what is now twenty tool pages was answering crawlers with a
// permission error. Emitting a real page here fixes it, because Apache serves a
// file when one exists.
//
// It also gives the tools a crawl path. Every one of them was reachable only
// from the footer, and as of 2026-08-17 the newer pages are discovered but not
// ranking — so discovery is the live problem, not competition.
//
// NOT BUILT TO RANK, deliberately. `free social media tools` is dominated by
// Buffer, Hootsuite and Later with the authority to hold it; `social media
// tools` is a listicle SERP. Writing this page for a term we cannot win would
// mean padding it. The honest job is navigation.
//
// Generated from the data files, so a new tool page appears here automatically.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import nameGenerators from '../data/name-generators.json'
import bioPages from '../data/bio-generator-pages.json'
import captionPages from '../data/caption-generator-pages.json'
import sizePages from '../data/size-pages.json'

const URL = '/tools'

/** Tools that are single pages rather than members of a data family. */
const SINGLES = [
  ['/tools/linkedin-text-formatter', 'LinkedIn text formatter', 'Bold and italic, with the accessibility cost shown'],
  ['/tools/emoji-translator', 'Emoji translator', 'Both directions, and how it is read aloud'],
  ['/tools/instagram-grid-planner', 'Instagram grid planner', 'Preview the 4:5 and square grids'],
  ['/tools/utm-builder', 'UTM builder', 'Campaign tracking links, with the casing warning'],
  ['/best-time-to-post', 'Best time to post', 'Posting times by platform and audience'],
  ['/tools/engagement-rate-calculator', 'Engagement rate calculator', 'The formula each platform actually uses'],
  ['/tools/content-calendar-template', 'Content calendar template', 'A CSV that imports straight back in'],
  ['/tools/social-media-report-template', 'Social media report template', 'A client report you can fill in'],
]

const label = x => x.h1 || x.seoTitle

export default function ToolsHub () {
  const groups = [
    ['Name and handle generators',
     nameGenerators.map(x => [`/tools/${x.slug}`, label(x), 'Free, no sign-up, unlimited'])],
    ['Bio generators',
     bioPages.map(x => [`/tools/${x.slug}`, label(x), 'Written to the character limit'])],
    ['Caption and post generators',
     captionPages.map(x => [`/tools/${x.slug}`, label(x), 'Drafted for the platform'])],
    ['Image sizes and resizing',
     sizePages.map(x => [`/tools/${x.slug}`, `${x.platformLabel} sizes`, 'Dimensions and a resizer'])],
    ['Planning and measurement', SINGLES],
  ].filter(([, list]) => list.length)

  const total = groups.reduce((n, [, list]) => n + list.length, 0)

  return (
    <>
      <Seo
        title='Free Social Media Tools'
        /* Kept identical to the /tools entry in seo-meta.json, which is what the
           prerenderer bakes in. A dynamic count here would silently drift from
           the static head every time a tool is added. */
        description='Free social media tools — name and username generators, bio and caption writers, image resizers, an engagement rate calculator and a calendar template.'
        path={URL}
        jsonLd={[{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Free social media tools',
          url: `${SITE_URL}${URL}`,
          hasPart: groups.flatMap(([, list]) => list.map(([href, title]) => ({
            '@type': 'WebApplication',
            name: title,
            url: `${SITE_URL}${href}`,
            applicationCategory: 'UtilitiesApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          }))),
        }]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='justify-content-center text-center'>
            <Col lg={9}>
              <h1 className='display-5 fw-bold mb-3'>Free social media tools</h1>
              <p className='lead mb-0'>
                {total} tools that do one job each and need no account — generators, resizers,
                calculators and templates.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE POINT ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            Every tool below runs without an account, without a credit card and without a
            trial timer. Most of them run entirely in your browser, which means nothing you
            type is sent anywhere.
          </p>
          <p className='text-muted fs-5 mb-0'>
            They exist because the jobs they do are the ones that stop people posting — a
            handle that keeps getting rejected, an image that is the wrong shape, a caption
            that will not start. They are useful on their own, and none of them expires.
          </p>
        </Container>
      </section>

      {/* ───────── THE DIRECTORY ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 1000 }}>
          {groups.map(([title, list]) => (
            <div className='mb-5' key={title}>
              <h2 className='fw-bold h4 mb-3'>{title}</h2>
              <Row className='g-3'>
                {list.map(([href, name, note]) => (
                  <Col md={6} lg={4} key={href}>
                    <a href={href} className='d-block p-3 rounded-3 text-decoration-none h-100'
                       style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                      <div className='fw-semibold mb-1'>{name}</div>
                      <div className='text-muted small'>{note}</div>
                    </a>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
          <p className='text-muted fs-5'>
            Greatly Social is a social media scheduler and content calendar. At Greatly Social
            you can describe your business once, let AI draft the month — captions and images —
            and schedule it to all eight networks from a single calendar, so posting stops
            depending on you being free at the right moment.
          </p>
          <Row className='g-3 mb-4'>
            {[['Describe it once, get the month', 'AI drafts the posts; you edit rather than write.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A real content calendar', 'Drag to reschedule. Empty days are obvious.'],
              ['Link in bio included', 'A hosted page that updates with your latest posts.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can do all of this in one place</h2>
          <p className='lead mb-4'>
            The tools are free and always will be. The product is what happens when you want
            the whole month handled instead of one task at a time.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
